import { useCallback, useEffect, useRef, useState } from 'react';
import { useClient } from 'urql';

import { JobDocument } from '@/graphql/subscriptions/generated/jobStatus';
import { JobEntityStatusEnum, LanguageEnum, ResourceStatusEnum } from '@/types/schema';

import { QuizLanguageInfoFragment } from '../../graphql/fragments/generated/QuizLanguageInfo';
import { useQuizLanguageInfoQuery } from '../../graphql/queries/generated/quizLanguageInfo';

interface Params {
  contentId: string;
}

const initialJobStatusValue = {} as Record<LanguageEnum, JobEntityStatusEnum>;

const useQuizLanguageInfo = ({ contentId }: Params) => {
  const client = useClient();
  const subscribed = useRef(new Set<LanguageEnum>());

  const [jobStatus, setJobStatus] =
    useState<Record<LanguageEnum, JobEntityStatusEnum>>(initialJobStatusValue);

  const [{ fetching, error, data }, refetch] = useQuizLanguageInfoQuery({
    variables: { contentId, versionStatus: ResourceStatusEnum.Draft },
    requestPolicy: 'network-only',
  });

  const quizLanguageInfo = data?.content as QuizLanguageInfoFragment | undefined;

  const subscribeToJobUpdates = useCallback(
    (jobScheduleId: string, language: LanguageEnum) => {
      const { unsubscribe } = client
        .subscription(
          JobDocument,
          { data: { jobScheduleId } },
          { requestPolicy: 'cache-and-network' },
        )
        .subscribe(({ data }) => {
          const status = data?.job?.status;
          if (status) {
            setJobStatus((prev) => ({ ...prev, [language]: status }));
          }
        });

      return unsubscribe;
    },
    [client],
  );

  useEffect(() => {
    const languageConfigs = quizLanguageInfo?.languageConfigs;
    if (!languageConfigs?.length) {
      return;
    }

    const newJobStatus = {} as Record<LanguageEnum, JobEntityStatusEnum>;
    const unsubscribers: (() => void)[] = [];
    // @ts-ignore
    languageConfigs.forEach(({ languageType, jobScheduleId, jobStatus }) => {
      if (languageType && jobStatus) {
        newJobStatus[languageType] = jobStatus;

        if (
          jobStatus === JobEntityStatusEnum.InProcess &&
          jobScheduleId &&
          !subscribed.current.has(languageType)
        ) {
          const unsubscribe = subscribeToJobUpdates(jobScheduleId, languageType);
          subscribed.current.add(languageType);
          unsubscribers.push(unsubscribe);
        }
      }
    });

    setJobStatus(newJobStatus);

    return () => {
      unsubscribers.forEach((unsub) => unsub());
      subscribed.current.clear();
    };
  }, [quizLanguageInfo, subscribeToJobUpdates]);

  return { fetching, error, data: quizLanguageInfo, refetch, jobStatus };
};

export default useQuizLanguageInfo;
